/**
 * @file 学员相关 API（对齐「思犇优学」`/pc/student` 文档）
 * @description 学员列表（按科目展平）/ 增删改 / 状态变更 / 重置密码 / 绑定教师。
 *
 * @whenToUse 学员管理页数据源。
 * @whenNotToUse 校区 / 教师接口（各自独立 api 文件）。
 */

import { del, getPage, post, put } from '@/lib/client.ts'

/** 学员科目详情（列表返回；亦作提交 subjectList 的基础结构） */
export type StudentSubjectItem = {
  /** 关联记录 ID */
  id: string
  /** 学员 id */
  studentId: string
  /** 科目 */
  subject: string
  /** 教材 id */
  textbookId: string
  /** 教材名称（仅列表返回） */
  textbookName: string
  /** 教育阶段 */
  educationStage: string
  /** 年级 */
  grade: string
  /** 学期 */
  semester: string
  /** 学习力等级 */
  learningGrade: string
  /** 学习力值 */
  learningForce: number
  /** 教师 id；未绑定为空串 */
  teacherId: string
  /** 教师名称 */
  teacherName: string
}

/** 学员列表行（分页；科目详情在 subjectList） */
export type StudentItem = {
  /** 学员 ID（后端 int64 雪花 id，JSON 序列化为字符串） */
  id: string
  /** 学员名称 */
  studentName: string
  /** 学员用户 id */
  userId: string
  /** 手机号（登录账号） */
  phone: string
  /** 点数 */
  points: number
  /** 状态（0-禁用 1-正常 2-试学） */
  status: '0' | '1' | '2'
  /** 允许 PAD 端调整基本信息（0-不允许 1-允许） */
  allowEditBasicInfo: number
  /** 创建时间 */
  createTime: string
  /** 科目详情列表 */
  subjectList: StudentSubjectItem[]
}

/** 学员列表查询参数 */
export type StudentListParams = {
  /** 页码，从 1 开始 */
  pageNum?: number
  /** 每页条数 */
  pageSize?: number
  /** 学员名称关键字 */
  studentName?: string
  /** 手机号关键字 */
  phone?: string
  /** 状态（0-禁用 1-正常 2-试学） */
  status?: string
  /** 创建时间起（YYYY-MM-DD） */
  createTimeStart?: string
  /** 创建时间止（YYYY-MM-DD） */
  createTimeEnd?: string
  /** 科目（按 subjectList 过滤） */
  subject?: string
  /** 教材 id（按 subjectList 过滤） */
  textbookId?: string
  /** 教育阶段 */
  educationStage?: string
  /** 年级 */
  grade?: string
  /** 学期 */
  semester?: string
  /** 学习力等级 */
  learningGrade?: string
  /** 学习力值（等值） */
  learningForce?: number
  /** 教师 id（数据范围过滤） */
  teacherId?: string
}

/** 新增学员请求体 */
export type StudentCreatePayload = {
  /** 学员名称 */
  studentName: string
  /** 手机号（登录账号） */
  phone: string
  /** 点数 */
  points: number
  /** 登录密码；为空则后端默认 123456 */
  password?: string
  /** 科目详情集合（至少一条） */
  subjectList: StudentSubjectPayload[]
}

/** 编辑学员请求体 */
export type StudentEditPayload = {
  /** 学员 ID */
  id: string
  /** 学员名称 */
  studentName: string
  /** 手机号 */
  phone: string
  /** 点数 */
  points: number
  /** 科目详情集合（至少一条） */
  subjectList: StudentSubjectPayload[]
}

/** 科目详情提交项 */
export type StudentSubjectPayload = {
  /** 关联记录 id；编辑已有详情时传入 */
  id?: string
  /** 科目 */
  subject: string
  /** 教材 id */
  textbookId: string
  /** 教育阶段 */
  educationStage: string
  /** 年级 */
  grade: string
  /** 学期 */
  semester: string
  /** 学习力等级 */
  learningGrade: string
  /** 学习力值 */
  learningForce: number
  /** 教师 id；可选 */
  teacherId?: string
}

/** 学员充值记录行（分页） */
export type StudentRechargeRecord = {
  /** 记录 ID */
  id: string
  /** 学员 id */
  studentId: string
  /** 学员姓名 */
  studentName: string
  /** 变更前点数 */
  beforePoints: number
  /** 变更后点数 */
  afterPoints: number
  /** 创建时间 */
  createTime: string
}

/**
 * 分页查询学员列表。
 * @param params 分页与筛选（studentName / phone / status / createTime）
 */
export function fetchStudentList(params?: StudentListParams) {
  return getPage<StudentItem>('/pc/student/list', { params })
}

/**
 * 新增学员（手机号作为登录账号）。
 * @param payload 学员字段
 */
export function createStudent(payload: StudentCreatePayload) {
  return post<null>('/pc/student', payload)
}

/**
 * 编辑学员。
 * @param payload 学员字段（含 id）
 */
export function updateStudent(payload: StudentEditPayload) {
  return put<null>('/pc/student', payload)
}

/**
 * 删除学员（支持批量，逗号分隔）。
 * @param ids 学员 ID 串
 */
export function deleteStudent(ids: string) {
  return del<null>(`/pc/student/${ids}`)
}

/**
 * 变更学员状态（启用 / 禁用 / 试学）。
 * @param id 学员 ID
 * @param status 目标状态（0-禁用 1-正常 2-试学）
 */
export function changeStudentStatus(id: string, status: '0' | '1' | '2') {
  return put<null>('/pc/student/changeStatus', { id, status })
}

/**
 * 重置学员账号密码（重置为 123456）。
 * @param id 学员 ID
 */
export function resetStudentPwd(id: string) {
  return post<null>('/pc/student/resetPwd', { id })
}

/**
 * 变更学员点数（正数充值，负数扣减，不能为 0）。
 * @param id 学员 ID
 * @param points 变更点数
 * @param remark 备注
 */
export function changeStudentPoints(id: string, points: number, remark?: string) {
  return post<null>('/pc/student/changePoints', { id, points, remark })
}

/**
 * 允许 / 禁止学员在 PAD 端调整基本信息。
 * @param studentId 学员 ID
 * @param allowEditBasicInfo 0-不允许 1-允许
 */
export function setStudentAllowEditBasicInfo(
  studentId: string,
  allowEditBasicInfo: 0 | 1,
) {
  return put<null>('/pc/student/allowEditBasicInfo', {
    studentId,
    allowEditBasicInfo,
  })
}

/**
 * 分页查询学员充值记录。
 * @param params 分页与学员筛选
 */
export function fetchStudentRechargeRecords(params: {
  studentId?: string
  pageNum?: number
  pageSize?: number
}) {
  return getPage<StudentRechargeRecord>(
    '/pc/student/rechargeRecord/rechargeRecord/list',
    { params },
  )
}

/**
 * 绑定 / 解绑学员科目的所属教师。
 * @param studentTeacherId 科目关联记录 ID
 * @param teacherId 教师 id；null 表示解绑
 */
export function bindStudentTeacher(
  studentTeacherId: string,
  teacherId: string | null,
) {
  return put<null>('/pc/student/bindTeacher', {
    studentTeacherId,
    teacherId,
  })
}
